import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  Grid,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Tag,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, CheckIcon, CloseIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { createFacilityFeeApi, deleteFacilityFeeApi, getFacilityFeesApi, updateFacilityFeeApi } from 'api/facilityFeeApi';
import Card from 'components/card/Card';
import { Currency } from 'components/Number';
import { CurrencyNumberInput } from 'components/NumberInput';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatAmountDisplay, parseAmount } from './facilityFeeFormat';

let _localId = 0;
const newDraftRow = () => ({ localId: ++_localId, dueDate: '', amount: '', comment: '' });

const COLS = '1fr 1fr 1fr 1fr 1fr 120px';

const FacilityFee = () => {
  const [savedFees, setSavedFees] = useState([]);
  const [draftRows, setDraftRows] = useState([newDraftRow()]);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const toast = useToast();
  const { customerId } = useParams();
  const navigate = useNavigate();

  useEffect(() => { load(); }, [customerId]);

  const load = async () => {
    try {
      const fees = await getFacilityFeesApi(customerId);
      setSavedFees(fees || []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load facility fees', status: 'error' });
    }
  };

  const addRow = () => setDraftRows(r => [...r, newDraftRow()]);
  const removeRow = (localId) => setDraftRows(r => r.filter(x => x.localId !== localId));
  const updateRow = (localId, field, value) =>
    setDraftRows(r => r.map(x => x.localId === localId ? { ...x, [field]: value } : x));

  const startEdit = (fee) => {
    setEditingId(fee.id);
    setEditDraft({
      dueDate: fee.due_date,
      amount: parseAmount(fee.amount),
      status: fee.status || 'not_paid',
      comment: fee.comment || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const deleteFee = async (fee) => {
    if (!window.confirm(`Delete fee for ${fee.due_date}?`)) return;
    setDeletingId(fee.id);
    try {
      await deleteFacilityFeeApi(customerId, fee.id);
      toast({ title: 'Fee deleted', status: 'success' });
      await load();
    } catch {
      toast({ title: 'Error', description: 'Failed to delete fee.', status: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const saveEdit = async () => {
    setUpdating(true);
    try {
      await updateFacilityFeeApi(customerId, editingId, {
        dueDate: editDraft.dueDate,
        amount: parseAmount(editDraft.amount),
        status: editDraft.status,
        comment: editDraft.comment,
      });
      toast({ title: 'Fee updated', status: 'success' });
      setEditingId(null);
      setEditDraft(null);
      await load();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update fee. Please try again.', status: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const hasInvalidRows = draftRows.some(
    (r) => !r.dueDate || parseAmount(r.amount) <= 0
  );
  const canSave = draftRows.length > 0 && !hasInvalidRows;
  const totalMonths = savedFees.length + draftRows.length;
  const amountToBeRepaid =
    savedFees
      .filter((fee) => fee.status !== 'paid')
      .reduce((sum, fee) => sum + parseAmount(fee.amount), 0) +
    draftRows.reduce((sum, row) => sum + parseAmount(row.amount), 0);

  const onSave = async () => {
    setSaving(true);
    try {
      await Promise.all(
        draftRows.map(r =>
          createFacilityFeeApi(customerId, {
            dueDate: r.dueDate,
            amount: parseAmount(r.amount),
            comment: r.comment,
          })
        )
      );
      toast({ title: 'Schedule saved', status: 'success' });
      setDraftRows([newDraftRow()]);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || '';
      toast({
        title: 'Error',
        description: msg.includes('already exists')
          ? 'A fee already exists for this customer on that date.'
          : 'Failed to save. Please try again.',
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box maxW="container.lg">
      <Breadcrumb mb={2} fontSize="sm" color="gray.500" separator="/">
        <BreadcrumbItem>
          <BreadcrumbLink onClick={() => navigate('/operations')}>
            Asset Tape
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Create Facility</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Heading fontSize="3xl" fontWeight="extrabold" color="navy.700" mb={6}>
        Create Facility
      </Heading>

      <Card p={6}>
        <Flex justify="space-between" align="flex-start" mb={1} gap={4} wrap="wrap">
          <Text fontWeight="bold" fontSize="md">Payment schedule</Text>
          <Flex direction="column" align="flex-end" gap={1}>
            <Text fontSize="sm" color="gray.500">
              {totalMonths} month{totalMonths !== 1 ? 's' : ''}
            </Text>
            <Text fontSize="sm" color="navy.700" fontWeight="semibold">
              Amount to be repaid:{' '}
              <Currency value={amountToBeRepaid} maximumFractionDigits={2} />
            </Text>
          </Flex>
        </Flex>
        <Text fontSize="sm" color="gray.500" mb={6}>
          Add the months and the amount to be paid. Click the edit icon to modify a saved fee.
        </Text>

        {/* Column headers */}
        <Grid templateColumns={COLS} gap={4} mb={2}>
          <Text fontSize="xs" fontWeight="semibold" color="gray.500" letterSpacing="wider" textTransform="uppercase">Date</Text>
          <Text fontSize="xs" fontWeight="semibold" color="gray.500" letterSpacing="wider" textTransform="uppercase">Amount to be paid</Text>
          <Text fontSize="xs" fontWeight="semibold" color="gray.500" letterSpacing="wider" textTransform="uppercase">Status</Text>
          <Text fontSize="xs" fontWeight="semibold" color="gray.500" letterSpacing="wider" textTransform="uppercase">Comment</Text>
          <Text fontSize="xs" fontWeight="semibold" color="gray.500" letterSpacing="wider" textTransform="uppercase">Transfer Date</Text>
          <Box />
        </Grid>

        {/* Saved rows */}
        {savedFees.map(fee => {
          const isEditing = editingId === fee.id;
          return (
            <Grid key={fee.id} templateColumns={COLS} gap={4} mb={3} alignItems="center">
              {isEditing ? (
                <>
                  <Input
                    type="date"
                    value={editDraft.dueDate}
                    onChange={e => setEditDraft(d => ({ ...d, dueDate: e.target.value }))}
                  />
                  <CurrencyNumberInput
                    value={editDraft.amount}
                    min={0}
                    showPrefix
                    onChange={(value) => setEditDraft((d) => ({ ...d, amount: value }))}
                  />
                  <Tag
                    colorScheme={editDraft.status === 'paid' ? 'green' : 'orange'}
                    size="sm"
                    w="fit-content"
                  >
                    {editDraft.status === 'paid' ? 'Paid' : 'Not Paid'}
                  </Tag>
                  <Textarea
                    value={editDraft.comment}
                    onChange={e => setEditDraft(d => ({ ...d, comment: e.target.value }))}
                    placeholder="Optional comment"
                    size="sm"
                    resize="none"
                    rows={1}
                  />
                  <Text fontSize="sm" color="gray.600">
                    {fee.transfer_date ? moment(fee.transfer_date, 'YYYY-MM-DD').format('MMM DD, YYYY') : '—'}
                  </Text>
                  <Flex gap={1}>
                    <IconButton
                      icon={<CheckIcon boxSize="9px" />}
                      size="sm"
                      colorScheme="green"
                      aria-label="Save"
                      isLoading={updating}
                      onClick={saveEdit}
                    />
                    <IconButton
                      icon={<CloseIcon boxSize="9px" />}
                      size="sm"
                      variant="outline"
                      aria-label="Cancel"
                      onClick={cancelEdit}
                    />
                  </Flex>
                </>
              ) : (
                <>
                  <Input
                    value={moment(fee.due_date, 'YYYY-MM-DD').format('MMM DD, YYYY')}
                    isReadOnly
                    bg="gray.50"
                    color="gray.600"
                  />
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" color="gray.400">$</InputLeftElement>
                    <Input
                      value={formatAmountDisplay(fee.amount)}
                      isReadOnly
                      pl={8}
                      bg="gray.50"
                      color="gray.600"
                    />
                  </InputGroup>
                  <Tag
                    colorScheme={fee.status === 'paid' ? 'green' : 'orange'}
                    size="sm"
                    w="fit-content"
                  >
                    {fee.status === 'paid' ? 'Paid' : 'Not Paid'}
                  </Tag>
                  <Text fontSize="sm" color="gray.600" noOfLines={1}>
                    {fee.comment || '—'}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {fee.transfer_date ? moment(fee.transfer_date, 'YYYY-MM-DD').format('MMM DD, YYYY') : '—'}
                  </Text>
                  <Flex gap={1}>
                    <IconButton
                      icon={<EditIcon boxSize="11px" />}
                      size="sm"
                      variant="outline"
                      aria-label="Edit"
                      isDisabled={editingId !== null || fee.status === 'paid'}
                      onClick={() => startEdit(fee)}
                    />
                    <IconButton
                      icon={<DeleteIcon boxSize="11px" />}
                      size="sm"
                      variant="outline"
                      colorScheme="red"
                      aria-label="Delete"
                      isDisabled={fee.status === 'paid' || deletingId !== null || editingId !== null}
                      isLoading={deletingId === fee.id}
                      onClick={() => deleteFee(fee)}
                    />
                  </Flex>
                </>
              )}
            </Grid>
          );
        })}

        {/* Draft rows — editable */}
        {draftRows.map(row => (
          <Grid key={row.localId} templateColumns={COLS} gap={4} mb={3} alignItems="center">
            <Input
              type="date"
              value={row.dueDate}
              onChange={e => updateRow(row.localId, 'dueDate', e.target.value)}
            />
            <CurrencyNumberInput
              value={row.amount === '' ? null : row.amount}
              min={0}
              showPrefix
              placeholder="0.00"
              onChange={(value) => updateRow(row.localId, 'amount', value ?? '')}
            />
            <Box />
            <Textarea
              value={row.comment}
              onChange={e => updateRow(row.localId, 'comment', e.target.value)}
              placeholder="Optional comment"
              size="sm"
              resize="none"
              rows={1}
            />
            <Box />
            <IconButton
              icon={<CloseIcon boxSize="9px" />}
              size="sm"
              variant="outline"
              aria-label="Remove row"
              onClick={() => removeRow(row.localId)}
            />
          </Grid>
        ))}

        <Button
          leftIcon={<AddIcon />}
          variant="outline"
          colorScheme="purple"
          size="xs"
          mt={1}
          onClick={addRow}
        >
          Add month
        </Button>

        <Box borderTop="1px solid" borderColor="gray.200" mt={6} mb={4} />

        <Flex justify="space-between" align="center">
          {hasInvalidRows ? (
            <Text fontSize="sm" color="orange.500">
              Pick a month and an amount greater than 0 for every new row to save.
            </Text>
          ) : (
            <Box />
          )}
          <Button
            colorScheme="purple"
            isDisabled={!canSave}
            isLoading={saving}
            onClick={onSave}
          >
            Save schedule
          </Button>
        </Flex>
      </Card>
    </Box>
  );
};

export default FacilityFee;
